import { useEffect, useState } from 'react';
import type { MediaAsset } from '@ghostscreen/contracts';
import { isVideoAsset } from './mediaPreview';

type MediaThumbProps = {
  asset: MediaAsset | undefined;
  className?: string;
};

function KindGlyph({ kind }: { kind: MediaAsset['kind'] | 'unknown' }) {
  return (
    <span className="grid h-full w-full place-items-center text-ink2" aria-hidden>
      {kind === 'video' ? (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <rect x="3" y="5" width="14" height="14" rx="2" />
          <path d="m17 10 4-2v8l-4-2" />
        </svg>
      ) : (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <circle cx="9" cy="9" r="2" />
          <path d="m21 15-5-5L5 21" />
        </svg>
      )}
    </span>
  );
}

function VideoBadge() {
  return (
    <span
      className="pointer-events-none absolute bottom-1.5 start-1.5 grid h-5 w-5 place-items-center rounded-md bg-black/65 text-white"
      aria-hidden
    >
      <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor">
        <path d="M8 5v14l11-7z" />
      </svg>
    </span>
  );
}

/**
 * Library / playlist thumbnail.
 * Ready assets expose `previewUrl` that prefers the worker JPEG poster
 * (`media.normalize` thumbnail variant), including for videos. Falls back to a
 * muted video frame when the signed URL is still the original media file.
 */
export function MediaThumb({ asset, className = '' }: MediaThumbProps) {
  const [imageBroken, setImageBroken] = useState(false);
  const [videoBroken, setVideoBroken] = useState(false);
  const previewUrl = asset?.previewUrl ?? null;
  const video = isVideoAsset(asset);

  useEffect(() => {
    setImageBroken(false);
    setVideoBroken(false);
  }, [asset?.id, previewUrl]);

  const showImage = Boolean(previewUrl) && !imageBroken;
  const showVideo = Boolean(previewUrl) && imageBroken && video && !videoBroken;

  // Media is always absolutely cropped inside this box. Callers must size the
  // box (fixed h/w, aspect-ratio, or absolute inset fill) — never the image.
  return (
    <span
      className={`relative block overflow-hidden ${className}`.trim()}
      style={{ background: 'var(--gs-panel2)' }}
    >
      {showImage ? (
        <img
          src={previewUrl!}
          alt=""
          draggable={false}
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
          onError={() => setImageBroken(true)}
        />
      ) : showVideo ? (
        <video
          src={previewUrl!}
          muted
          playsInline
          preload="metadata"
          className="absolute inset-0 h-full w-full object-cover"
          onLoadedData={(event) => {
            const el = event.currentTarget;
            if (el.duration > 0.15 && el.currentTime < 0.05) {
              try {
                el.currentTime = 0.1;
              } catch {
                /* seek optional — first frame is enough */
              }
            }
          }}
          onError={() => setVideoBroken(true)}
        />
      ) : (
        <span className="absolute inset-0">
          <KindGlyph kind={asset?.kind ?? 'unknown'} />
        </span>
      )}
      {video && (showImage || showVideo) ? <VideoBadge /> : null}
    </span>
  );
}
